import React from "react"
import { Button } from "@material-ui/core"
import { FormattedMessage } from "react-intl"
import { createStyles, makeStyles } from "@material-ui/core/styles"
import { theme } from "../../../layouts/MainFrame"
import { Plan, RecommendedPricing } from "../../../types"
import { Locale } from "../../../locale"
import { useTrack } from "@hackler/react-sdk"
import useLoginPopup from "../../../hooks/useLoginPopup"

const useStyles = makeStyles(() =>
  createStyles({
    displayer: {
      padding: "27px 40px 43px",
      background: "#F4F7FF",
      width: "490px",
      border: "1px solid #ededed",
      borderTopLeftRadius: "0",
      borderTopRightRadius: "20px",
      borderBottomLeftRadius: "0",
      borderBottomRightRadius: "20px",
      [theme.breakpoints.down("sm")]: {
        padding: "30px 26px 23px",
        width: "100%",
        height: "268px",
        borderTopLeftRadius: "0",
        borderTopRightRadius: "0",
        borderBottomLeftRadius: "20px",
        borderBottomRightRadius: "20px",
      },
      "& dl": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        margin: "0",
        "&:first-child": {
          marginBottom: "14px",
        },
      },
      "& dt": {
        fontSize: "18px",
        color: "rgba(0,0,0,0.6)",
        [theme.breakpoints.down("sm")]: {
          fontSize: "14px",
        },
      },
      "& dd": {
        margin: 0,
        fontSize: "20px",
        color: "#1C1C1C",
        "& strong": {
          fontSize: "27px",
          [theme.breakpoints.down("sm")]: {
            fontSize: "18px",
          },
        },
      },
    },
    ul: {
      margin: 0,
      padding: 0,
      listStyle: "none",
      "& li": {
        color: "#8E8E8E",
        fontSize: "13px",
        [theme.breakpoints.down("sm")]: {
          fontSize: "10px",
        },
      },
    },
    buttonWrap: {
      margin: "28px 0 18px",
      [theme.breakpoints.down("sm")]: {
        margin: "16px 0 16px",
      },
    },
    button: {
      textTransform: "none",
      borderRadius: 12,
      fontSize: "18px",
      padding: "12px 20px",
      height: "54px",
    },
  })
)

interface CalculationDisplayerProps {
  recommendedPricing: RecommendedPricing
  inquire: () => void
}

export default function CalculationDisplayer({ recommendedPricing, inquire }: CalculationDisplayerProps) {
  const classes = useStyles()
  const track = useTrack()
  const lang = Locale.getLangCode()
  const loginPopup = useLoginPopup()
  const { plan, monthlyExpectedPrice } = recommendedPricing

  const price = () => {
    const { minimum, maximum } = monthlyExpectedPrice
    if (lang === "ko") {
      return (
        <>
          <strong>
            {minimum / 10000} ~ {maximum / 10000}
          </strong>{" "}
          만원
        </>
      )
    }
    return (
      <strong>
        $ {minimum / 1000} ~ {maximum / 1000}
      </strong>
    )
  }

  const planText = (plan: Plan) => {
    switch (plan) {
      case "FREE":
        return <FormattedMessage id={"free_plan"} />
      case "PRO":
        return <FormattedMessage id={"pro_plan"} />
      case "ENTERPRISE":
        return <FormattedMessage id={"enterprise_plan"} />
      default:
        return ""
    }
  }

  const isEnterprisePlan = () => plan === Plan.ENTERPRISE

  return (
    <div className={classes.displayer}>
      <dl>
        <dt>
          <FormattedMessage id="pricing_calculator_recommended_plan" />
        </dt>
        <dd>
          <strong>{planText(plan)}</strong>
        </dd>
      </dl>
      <dl>
        <dt>
          <FormattedMessage id={"pricing_calculator_estimated_monthly_price"} values={{ br: <br /> }} />
        </dt>
        <dd>{price()}</dd>
      </dl>
      <div className={classes.buttonWrap}>
        <Button
          color={"primary"}
          variant={"contained"}
          onClick={() => {
            if (isEnterprisePlan()) {
              track({ key: "homepage_pricing_calculator_enterprise_plan_question_click" })
              inquire()
              return
            }

            track({ key: "homepage_pricing_calculator_free_trial_click" })
            loginPopup()
          }}
          className={classes.button}
          style={{ boxShadow: "none" }}
          fullWidth={true}>
          <strong>
            {isEnterprisePlan() ? (
              <FormattedMessage id={"btn_inquire"} />
            ) : (
              <FormattedMessage id={"BTN_START_ABTEST"} />
            )}
          </strong>
        </Button>
      </div>
      <ul className={classes.ul}>
        <li>
          <FormattedMessage id={"pricing_calculator_notice"} />
        </li>
      </ul>
    </div>
  )
}
